import { useEffect, useId, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Button } from '../../../components/Button';
import { ModalCloseButton } from '../../../components/ModalCloseButton';
import { TextInput } from '../../../components/TextInput';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import {
  AdminInviteError,
  createSetupInvitation,
  inviteToOrganization,
  resendSetupInvitation,
  resolveInvite,
} from '../../../services/adminInvites';
import type { ResolveInviteResponse } from '../../../types/adminInvites';
import { formatAnnualPrice } from '../../signup/signupFormat';
import { roleLabelFromKey } from '../adminRoles';
import { InviteMatchStep } from './InviteMatchStep';
import { InviteNewProgramStep } from './InviteNewProgramStep';
import { commonMessage } from './inviteForm';
import type { InviteDone } from './inviteTypes';
import './InviteUserDialog.css';

type Step =
  | { kind: 'email' }
  | { kind: 'resolving' }
  | { kind: 'blocked'; message: string; organizationName: string | null }
  | { kind: 'match'; resolved: ResolveInviteResponse }
  | { kind: 'new_program'; resolved: ResolveInviteResponse; fromMatch: boolean }
  | { kind: 'done'; done: InviteDone };

interface InviteUserDialogProps {
  onClose: () => void;
  /** Something was sent, so lists that show invitations should re-read. */
  onSent: () => void;
}

/**
 * The platform admin's "Invite user": type an address, and the API says whether it already has an
 * account, matches a program that exists, or needs a new one. Mount it to open it; state resets
 * with each mount.
 */
export const InviteUserDialog = ({ onClose, onSent }: InviteUserDialogProps) => {
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();

  useDialogFocus({ isOpen: true, dialogRef, onClose, lockScroll: true });

  /**
   * Each step replaces the body, so the control that was focused unmounts. Focus goes to the new
   * step's heading, or to the address field when the admin is back at the start.
   */
  useEffect(() => {
    if (step.kind === 'email') emailRef.current?.focus();
    else headingRef.current?.focus();
  }, [step.kind]);

  const resolve = async (event: React.FormEvent) => {
    event.preventDefault();
    const typed = email.trim();
    if (!typed) {
      setEmailError('Enter an email address.');
      return;
    }

    setEmailError(null);
    setStep({ kind: 'resolving' });

    try {
      const resolved = await resolveInvite(typed);
      if (resolved.outcome === 'blocked' && resolved.blocked) {
        setStep({
          kind: 'blocked',
          message: resolved.blocked.message,
          organizationName: resolved.blocked.organization?.name ?? null,
        });
      } else if (resolved.outcome === 'existing_org') {
        setStep({ kind: 'match', resolved });
      } else {
        setStep({ kind: 'new_program', resolved, fromMatch: false });
      }
    } catch (err: unknown) {
      const failure = err instanceof AdminInviteError ? err : new AdminInviteError('failed', '');
      setEmailError(
        failure.kind === 'invalid'
          ? 'Enter a valid email address.'
          : failure.kind === 'send_failed'
            ? 'We could not check that address right now. Try again.'
            : failure.kind === 'throttled'
              ? // The shared copy is about sending; a lookup is throttled per admin, not per address.
                'Too many lookups. Wait a minute and try again.'
              : commonMessage(failure.kind, failure.message)
      );
      setStep({ kind: 'email' });
    }
  };

  const startOver = () => {
    setEmail('');
    setEmailError(null);
    setStep({ kind: 'email' });
  };

  const finish = (done: InviteDone) => {
    // A saved-but-unsent row is still a new pending row, so the lists re-read either way.
    onSent();
    setStep({ kind: 'done', done });
  };

  const block = (message: string, organizationName: string | null) =>
    setStep({ kind: 'blocked', message, organizationName });

  return (
    <div className="invite-user-overlay">
      <button
        type="button"
        className="invite-user-overlay__scrim"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        data-testid="invite-user-scrim"
      />
      <div
        className="invite-user-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="invite-user-dialog__header">
          <h2 className="invite-user-dialog__title" id={titleId}>
            Invite user
          </h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        {step.kind === 'email' && (
          <form className="invite-user-dialog__body" onSubmit={resolve} noValidate>
            <p className="invite-user-dialog__text">
              We check whether they already have an account or a program before anything is sent.
            </p>
            <TextInput
              ref={emailRef}
              label="Email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError ?? undefined}
              data-testid="invite-email"
            />
            <div className="invite-user-dialog__actions">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" data-testid="invite-continue">
                Continue
              </Button>
            </div>
          </form>
        )}

        {step.kind === 'resolving' && (
          <div className="invite-user-dialog__body">
            <h3 className="invite-user-dialog__step-title" ref={headingRef} tabIndex={-1}>
              Checking that address
            </h3>
            <p className="invite-user-dialog__text" role="status">
              Looking up {email.trim()}...
            </p>
          </div>
        )}

        {step.kind === 'blocked' && (
          <div className="invite-user-dialog__body">
            <h3 className="invite-user-dialog__step-title" ref={headingRef} tabIndex={-1}>
              {step.organizationName
                ? `Already signed up with ${step.organizationName}`
                : 'This address already has an account'}
            </h3>
            <p className="invite-user-dialog__text">{step.message}</p>
            <div className="invite-user-dialog__actions">
              <Button type="button" variant="secondary" onClick={startOver}>
                Try another address
              </Button>
              <Button type="button" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {step.kind === 'match' && (
          <div className="invite-user-dialog__body">
            <InviteMatchStep
              resolved={step.resolved}
              headingRef={headingRef}
              onCreateNew={() =>
                setStep({ kind: 'new_program', resolved: step.resolved, fromMatch: true })
              }
              onDone={finish}
              onBlocked={block}
            />
          </div>
        )}

        {step.kind === 'new_program' && (
          <InviteNewProgramStep
            resolved={step.resolved}
            headingRef={headingRef}
            onBack={
              step.fromMatch
                ? () => setStep({ kind: 'match', resolved: step.resolved })
                : undefined
            }
            onDone={finish}
            onBlocked={block}
          />
        )}

        {step.kind === 'done' && (
          <InviteDoneStep
            done={step.done}
            headingRef={headingRef}
            onResent={(done) => setStep({ kind: 'done', done })}
            onAnother={startOver}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};

/** What was sent, and a resend when the email did not go out. */
const InviteDoneStep = ({
  done,
  headingRef,
  onResent,
  onAnother,
  onClose,
}: {
  done: InviteDone;
  headingRef: RefObject<HTMLHeadingElement | null>;
  onResent: (done: InviteDone) => void;
  onAnother: () => void;
  onClose: () => void;
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);

    try {
      if (done.kind === 'org') {
        // Re-posting re-arms the open row and sends it again. There is no admin resend route.
        await inviteToOrganization(done.organizationId, { email: done.email, role: done.role });
        onResent({ ...done, sendFailed: false });
      } else {
        const row = done.setupInvitationId
          ? await resendSetupInvitation(done.setupInvitationId)
          : await createSetupInvitation(done.request);
        onResent({ ...done, sendFailed: false, setupInvitationId: row.id });
      }
    } catch (err: unknown) {
      const failure = err instanceof AdminInviteError ? err : new AdminInviteError('failed', '');
      setError(
        failure.kind === 'send_failed'
          ? 'The email still did not go out. Try again in a moment.'
          : commonMessage(failure.kind, failure.message)
      );
    } finally {
      setBusy(false);
    }
  };

  const title =
    done.kind === 'org'
      ? done.sendFailed
        ? 'Invitation saved, email not sent'
        : 'Invitation sent'
      : done.sendFailed
        ? 'Setup invitation saved, email not sent'
        : 'Setup invitation sent';

  const summary =
    done.kind === 'org'
      ? `${done.email} is invited to ${done.organizationName} as ${roleLabelFromKey(done.role)}.`
      : `${done.email} is invited to create ${done.programName ?? 'a new program'}.`;

  const payment =
    done.kind === 'setup'
      ? done.requiresPayment
        ? done.priceCents !== null
          ? `They pay ${formatAnnualPrice(done.priceCents, 'usd')} after they sign in.`
          : 'They pay after they sign in.'
        : 'The program is free.'
      : null;

  return (
    <div className="invite-user-dialog__body">
      <h3 className="invite-user-dialog__step-title" ref={headingRef} tabIndex={-1}>
        {title}
      </h3>
      <p
        className={`invite-user-dialog__notice${done.sendFailed ? '' : ' invite-user-dialog__notice--success'}`}
        data-testid="invite-done-summary"
      >
        {summary} {payment}{' '}
        {done.sendFailed
          ? 'The email did not go out, so they have nothing yet.'
          : 'The link works for 7 days.'}
      </p>

      {error && (
        <p className="invite-user-dialog__error" role="alert">
          {error}
        </p>
      )}

      <div className="invite-user-dialog__actions">
        {done.sendFailed && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => void resend()}
            aria-disabled={busy}
            aria-busy={busy}
            data-testid="invite-resend"
          >
            Resend
          </Button>
        )}
        <Button type="button" variant="secondary" onClick={onAnother}>
          Invite someone else
        </Button>
        <Button type="button" onClick={onClose} data-testid="invite-done">
          Done
        </Button>
      </div>
    </div>
  );
};
